// src/pages/finance-portal/budgets/BudgetDetail.js
//
// Budget detail with line editor + version control + workflow.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, IconButton, Card, Chip, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, MenuItem, Select, FormControl, InputLabel, Tooltip,
    Skeleton, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
    InputAdornment, CircularProgress, Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import ReplayIcon from '@mui/icons-material/Replay';
import LockIcon from '@mui/icons-material/Lock';
import BarChartIcon from '@mui/icons-material/BarChart';
import HistoryIcon from '@mui/icons-material/History';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT:     { bg: '#f1f5f9', color: '#64748b' },
    IN_REVIEW: { bg: '#fef9c3', color: '#854d0e' },
    APPROVED:  { bg: '#dcfce7', color: '#166534' },
    REJECTED:  { bg: '#fecaca', color: '#991b1b' },
    CLOSED:    { bg: '#e0e7ff', color: '#4338ca' },
};

function fmtMoney(val, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, maximumFractionDigits: 2,
    }).format(val || 0);
}

function emptyLine() {
    return { gl_account_id: '', period: '', amount: '', department: '', notes: '' };
}

export default function BudgetDetail() {
    const { budgetId } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [budget, setBudget] = useState(null);
    const [activeVersionId, setActiveVersionId] = useState(null);
    const [lines, setLines] = useState([]);
    const [savedLineSnapshot, setSavedLineSnapshot] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [rejectDialog, setRejectDialog] = useState({ open: false, reason: '' });
    const [versionDialog, setVersionDialog] = useState({ open: false, label: '', notes: '', copy: true });

    const fetchBudget = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(
                `/api/finance/budgets/${budgetId}?include_lines=true`
            );
            if (res.data.success) {
                const b = res.data.data;
                setBudget(b);
                const active = (b.versions || []).find(v => v.is_active) || (b.versions || [])[0];
                setActiveVersionId(active?.id || null);
                const ls = (active?.lines || []).map(l => ({
                    id: l.id,
                    gl_account_id: l.gl_account_id || '',
                    period: l.period || '',
                    amount: String(l.amount ?? ''),
                    department: l.department || '',
                    notes: l.notes || '',
                }));
                setLines(ls);
                setSavedLineSnapshot(JSON.parse(JSON.stringify(ls)));
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load budget');
        } finally {
            setLoading(false);
        }
    }, [budgetId]);

    const fetchGlAccounts = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
            if (res.data.success) setGlAccounts(res.data.data || []);
        } catch { /* silent */ }
    }, []);

    useEffect(() => { fetchBudget(); fetchGlAccounts(); }, [fetchBudget, fetchGlAccounts]);

    const switchVersion = async (versionId) => {
        if (!versionId || versionId === activeVersionId) return;
        try {
            await axiosInstance.get(`/api/finance/budgets/${budgetId}/versions/${versionId}/lines`).then(r => {
                const ls = (r.data?.data || []).map(l => ({
                    id: l.id,
                    gl_account_id: l.gl_account_id || '',
                    period: l.period || '',
                    amount: String(l.amount ?? ''),
                    department: l.department || '',
                    notes: l.notes || '',
                }));
                setLines(ls);
                setSavedLineSnapshot(JSON.parse(JSON.stringify(ls)));
                setActiveVersionId(versionId);
            });
        } catch (err) {
            enqueueSnackbar('Failed to load version lines', { variant: 'error' });
        }
    };

    const setActiveOnServer = async () => {
        if (!activeVersionId) return;
        try {
            await axiosInstance.post(
                `/api/finance/budgets/${budgetId}/versions/${activeVersionId}/activate`
            );
            enqueueSnackbar('Active version updated', { variant: 'success' });
            fetchBudget();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const isEditable = budget && (budget.status === 'DRAFT' || budget.status === 'REJECTED');

    const totalAmount = useMemo(() =>
        lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0), [lines]);

    const dirty = useMemo(() => {
        if (lines.length !== savedLineSnapshot.length) return true;
        for (let i = 0; i < lines.length; i++) {
            const a = lines[i], b = savedLineSnapshot[i];
            if (a.gl_account_id !== b.gl_account_id ||
                a.period !== b.period ||
                a.amount !== b.amount ||
                a.department !== b.department ||
                a.notes !== b.notes) return true;
        }
        return false;
    }, [lines, savedLineSnapshot]);

    const updateLine = (idx, field, value) => {
        setLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
        if (errors[`lines[${idx}].${field}`]) {
            setErrors(prev => { const n = { ...prev }; delete n[`lines[${idx}].${field}`]; return n; });
        }
    };
    const addLine = () => setLines(prev => [...prev, emptyLine()]);
    const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));

    const validateLines = () => {
        const e = {};
        lines.forEach((l, i) => {
            if (!l.gl_account_id) e[`lines[${i}].gl_account_id`] = 'Required';
            if (!l.period) e[`lines[${i}].period`] = 'Required';
            else if (!/^\d{4}(-\d{2})?$/.test(l.period)) e[`lines[${i}].period`] = "Use 'YYYY' or 'YYYY-MM'";
            const amt = parseFloat(l.amount);
            if (l.amount === '' || isNaN(amt)) e[`lines[${i}].amount`] = 'Required number';
        });
        return e;
    };

    const saveLines = async () => {
        if (!activeVersionId) return;
        const localErrors = validateLines();
        if (Object.keys(localErrors).length > 0) {
            setErrors(localErrors);
            enqueueSnackbar('Fix line errors before saving', { variant: 'warning' });
            return;
        }
        setSaving(true);
        setErrors({});
        try {
            const payload = {
                lines: lines.map(l => ({
                    gl_account_id: parseInt(l.gl_account_id, 10),
                    period: l.period,
                    amount: parseFloat(l.amount),
                    department: l.department || null,
                    notes: l.notes || null,
                })),
            };
            const res = await axiosInstance.put(
                `/api/finance/budgets/${budgetId}/versions/${activeVersionId}/lines`,
                payload,
            );
            enqueueSnackbar(res.data?.message || 'Saved', { variant: 'success' });
            fetchBudget();
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                enqueueSnackbar('Validation failed — see line errors', { variant: 'warning' });
            } else {
                enqueueSnackbar(err.response?.data?.message || 'Failed to save', { variant: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    // ---- Workflow actions ----
    const transition = async (action, body = null) => {
        try {
            const res = await axiosInstance.post(
                `/api/finance/budgets/${budgetId}/${action}`,
                body || {},
            );
            enqueueSnackbar(res.data?.message || 'Done', { variant: 'success' });
            fetchBudget();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const submitReject = async () => {
        if (!rejectDialog.reason.trim()) {
            enqueueSnackbar('Reason is required', { variant: 'warning' });
            return;
        }
        await transition('reject', { reason: rejectDialog.reason.trim() });
        setRejectDialog({ open: false, reason: '' });
    };

    const createVersion = async () => {
        if (!versionDialog.label.trim()) {
            enqueueSnackbar('Version label is required', { variant: 'warning' });
            return;
        }
        try {
            const payload = {
                label: versionDialog.label.trim(),
                notes: versionDialog.notes || null,
            };
            if (versionDialog.copy && activeVersionId) {
                payload.copy_from_version_id = activeVersionId;
            }
            await axiosInstance.post(`/api/finance/budgets/${budgetId}/versions`, payload);
            enqueueSnackbar('Version created', { variant: 'success' });
            setVersionDialog({ open: false, label: '', notes: '', copy: true });
            fetchBudget();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b',
                        textTransform: 'uppercase', bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0',
                        letterSpacing: '0.05em' };

    if (loading) {
        return (
            <Box sx={{ p: 4, fontFamily }}>
                <Skeleton height={60} />
                <Skeleton height={400} sx={{ mt: 2 }} />
            </Box>
        );
    }

    if (!budget) {
        return (
            <Box sx={{ p: 4, fontFamily }}>
                <Alert severity="error">{error || 'Budget not found'}</Alert>
                <Button onClick={() => navigate('/finance-portal/budgets')} sx={{ mt: 2, fontFamily }}>
                    Back to Budgets
                </Button>
            </Box>
        );
    }

    const statusStyle = STATUS_COLORS[budget.status] || STATUS_COLORS.DRAFT;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <IconButton onClick={() => navigate('/finance-portal/budgets')}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            {budget.budget_number} · FY {budget.fiscal_year}
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            {budget.name}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                    <Chip label={budget.status.replace('_', ' ')} size="small"
                          sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                bgcolor: statusStyle.bg, color: statusStyle.color }} />
                    <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem' }}>
                        {budget.period_start} → {budget.period_end}
                    </Typography>
                    <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem' }}>
                        · Method: {budget.method}
                    </Typography>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)}
                                  sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {budget.rejection_reason && budget.status === 'REJECTED' && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>
                        <strong>Rejected:</strong> {budget.rejection_reason}
                    </Alert>
                )}

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel sx={{ fontFamily, fontSize: '0.85rem' }}>Version</InputLabel>
                            <Select
                                value={activeVersionId || ''}
                                label="Version"
                                onChange={e => switchVersion(e.target.value)}
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                {(budget.versions || []).map(v => (
                                    <MenuItem key={v.id} value={v.id} sx={{ fontFamily }}>
                                        v{v.version_number} — {v.label} {v.is_active ? '(active)' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button startIcon={<HistoryIcon />} variant="outlined" size="small"
                                onClick={() => setVersionDialog({ open: true, label: '', notes: '', copy: true })}
                                disabled={budget.status === 'CLOSED'}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '8px' }}>
                            New Version
                        </Button>

                        <Button size="small" variant="text"
                                onClick={setActiveOnServer}
                                disabled={!activeVersionId}
                                sx={{ fontFamily, textTransform: 'none' }}>
                            Make This Active
                        </Button>

                        <Box sx={{ flexGrow: 1 }} />

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {isEditable && (
                                <Button startIcon={<SaveIcon />} variant="contained" size="small" onClick={saveLines}
                                        disabled={saving || !dirty}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                              borderRadius: '10px', bgcolor: ACCENT,
                                              '&:hover': { bgcolor: '#16a34a' } }}>
                                    {saving ? 'Saving...' : 'Save Lines'}
                                </Button>
                            )}
                            {budget.status === 'DRAFT' && (
                                <Button startIcon={<SendIcon />} variant="outlined" size="small"
                                        onClick={() => transition('submit')}
                                        disabled={lines.length === 0 || dirty}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                                    Submit for Review
                                </Button>
                            )}
                            {budget.status === 'IN_REVIEW' && (
                                <>
                                    <Button startIcon={<CheckIcon />} variant="contained" size="small"
                                            onClick={() => transition('approve')}
                                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                                  borderRadius: '10px', bgcolor: '#16a34a' }}>
                                        Approve
                                    </Button>
                                    <Button startIcon={<BlockIcon />} variant="outlined" size="small"
                                            onClick={() => setRejectDialog({ open: true, reason: '' })}
                                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                                  borderRadius: '10px', color: '#ef4444', borderColor: '#fecaca' }}>
                                        Reject
                                    </Button>
                                    <Button startIcon={<ReplayIcon />} variant="text" size="small"
                                            onClick={() => transition('reopen')}
                                            sx={{ fontFamily, textTransform: 'none' }}>
                                        Reopen
                                    </Button>
                                </>
                            )}
                            {budget.status === 'APPROVED' && (
                                <>
                                    <Button startIcon={<BarChartIcon />} variant="contained" size="small"
                                            onClick={() => navigate(`/finance-portal/budgets/${budget.id}/variance`)}
                                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                                  borderRadius: '10px', bgcolor: '#3b82f6' }}>
                                        Variance Report
                                    </Button>
                                    <Button startIcon={<LockIcon />} variant="outlined" size="small"
                                            onClick={() => transition('close')}
                                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                                        Close Budget
                                    </Button>
                                </>
                            )}
                            {budget.status === 'REJECTED' && (
                                <Button startIcon={<ReplayIcon />} variant="contained" size="small"
                                        onClick={() => transition('reopen')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                              borderRadius: '10px', bgcolor: ACCENT }}>
                                    Reopen to Edit
                                </Button>
                            )}
                            {budget.status === 'CLOSED' && (
                                <Button startIcon={<BarChartIcon />} variant="contained" size="small"
                                        onClick={() => navigate(`/finance-portal/budgets/${budget.id}/variance`)}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                              borderRadius: '10px', bgcolor: '#3b82f6' }}>
                                    Variance Report
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0',
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>
                                Budget Lines
                            </Typography>
                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                Total: <strong style={{ color: '#1e293b' }}>{fmtMoney(totalAmount, budget.currency)}</strong>
                                {' · '}{lines.length} line(s)
                            </Typography>
                        </Box>
                        {isEditable && (
                            <Button startIcon={<AddIcon />} size="small" variant="outlined" onClick={addLine}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                                Add Line
                            </Button>
                        )}
                    </Box>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...labelCell, width: 320 }}>GL Account</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 130 }}>Period</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 160 }}>Amount</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 160 }}>Department</TableCell>
                                    <TableCell sx={labelCell}>Notes</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 56 }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lines.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                No lines yet. Click "Add Line" to begin.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : lines.map((l, i) => (
                                    <TableRow key={i}>
                                        <TableCell>
                                            <Autocomplete
                                                size="small" disableClearable={false}
                                                options={glAccounts}
                                                getOptionLabel={a => a ? `${a.account_number} — ${a.name}` : ''}
                                                value={glAccounts.find(a => a.id === parseInt(l.gl_account_id, 10)) || null}
                                                onChange={(_, val) => updateLine(i, 'gl_account_id', val?.id || '')}
                                                disabled={!isEditable}
                                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                                renderInput={(params) => (
                                                    <TextField {...params} placeholder="Select account…"
                                                               error={!!errors[`lines[${i}].gl_account_id`]}
                                                               helperText={errors[`lines[${i}].gl_account_id`] || ''}
                                                               InputProps={{ ...params.InputProps,
                                                                             sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" fullWidth placeholder="YYYY-MM"
                                                       value={l.period}
                                                       onChange={e => updateLine(i, 'period', e.target.value)}
                                                       disabled={!isEditable}
                                                       error={!!errors[`lines[${i}].period`]}
                                                       helperText={errors[`lines[${i}].period`] || ''}
                                                       InputProps={{ sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" fullWidth type="number"
                                                       value={l.amount}
                                                       onChange={e => updateLine(i, 'amount', e.target.value)}
                                                       disabled={!isEditable}
                                                       error={!!errors[`lines[${i}].amount`]}
                                                       helperText={errors[`lines[${i}].amount`] || ''}
                                                       InputProps={{
                                                           startAdornment: <InputAdornment position="start"
                                                               sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.82rem' } }}>
                                                               {budget.currency === 'USD' ? '$' : budget.currency}
                                                           </InputAdornment>,
                                                           sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' },
                                                       }} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" fullWidth value={l.department}
                                                       onChange={e => updateLine(i, 'department', e.target.value)}
                                                       disabled={!isEditable}
                                                       InputProps={{ sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" fullWidth value={l.notes}
                                                       onChange={e => updateLine(i, 'notes', e.target.value)}
                                                       disabled={!isEditable}
                                                       InputProps={{ sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                        </TableCell>
                                        <TableCell>
                                            {isEditable && (
                                                <IconButton size="small" onClick={() => removeLine(i)}
                                                            sx={{ color: '#ef4444' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            <Dialog open={rejectDialog.open}
                    onClose={() => setRejectDialog({ open: false, reason: '' })}
                    PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Reject Budget</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth multiline rows={3}
                               label="Rejection reason"
                               value={rejectDialog.reason}
                               onChange={e => setRejectDialog(prev => ({ ...prev, reason: e.target.value }))}
                               InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                               InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRejectDialog({ open: false, reason: '' })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitReject}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: '#ef4444',
                                  '&:hover': { bgcolor: '#dc2626' } }}>
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={versionDialog.open}
                    onClose={() => setVersionDialog({ open: false, label: '', notes: '', copy: true })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>New Version</DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <TextField label="Version Label *" size="small" fullWidth autoFocus
                                   value={versionDialog.label}
                                   onChange={e => setVersionDialog(p => ({ ...p, label: e.target.value }))}
                                   placeholder="e.g. Revised Q2"
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <TextField label="Notes" size="small" fullWidth multiline rows={2}
                                   value={versionDialog.notes}
                                   onChange={e => setVersionDialog(p => ({ ...p, notes: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <input type="checkbox" id="copy-lines"
                                   checked={versionDialog.copy}
                                   onChange={e => setVersionDialog(p => ({ ...p, copy: e.target.checked }))} />
                            <label htmlFor="copy-lines"
                                   style={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                Copy lines from current version
                            </label>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setVersionDialog({ open: false, label: '', notes: '', copy: true })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={createVersion}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: ACCENT,
                                  '&:hover': { bgcolor: '#16a34a' } }}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
